
import React, { useState } from 'react';
import { ArrowLeft, Calendar, ChevronDown, ChevronUp, Trophy, CheckCircle2, XCircle, SkipForward, Layers } from 'lucide-react';
import { User, TournamentResult } from '../types';

interface TournamentHistoryProps {
  user: User;
  onBack: () => void;
}

const TournamentHistory: React.FC<TournamentHistoryProps> = ({ user, onBack }) => {
  const history = user.tournamentHistory || [];
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
      switch(status) {
          case 'success': return <CheckCircle2 size={16} className="text-green-500" />;
          case 'loss': return <XCircle size={16} className="text-red-500" />;
          case 'skip': return <SkipForward size={16} className="text-zinc-500" />;
          default: return <div className="w-4 h-4 rounded-full border border-zinc-600" />;
      }
  };

  const getCategoryLabel = (cat: string) => {
      if (cat === 'PRELIM1') return 'PRELIM 1';
      if (cat === 'PRELIM2') return 'PRELIM 2';
      return 'FINALS';
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-2 md:p-4 flex flex-col h-full text-white pt-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 shrink-0 relative z-10">
        <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft size={20} /> <span className="hidden sm:inline font-orbitron text-xs tracking-widest uppercase">Back</span>
        </button>
        <h2 className="text-xl md:text-3xl font-orbitron font-bold neon-glow-blue uppercase tracking-widest">HISTORY / 历史记录</h2>
        <div className="w-8"></div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar space-y-4 pr-1">
          {history.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-zinc-500 font-rajdhani">
                  <Calendar size={48} className="mb-4 opacity-20" />
                  <p>No tournament records. / 暂无记录。</p>
              </div>
          ) : (
              history.map((record) => (
                  <div key={record.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-zinc-700 transition-colors">
                      {/* Summary Header */}
                      <div 
                        onClick={() => toggleExpand(record.id)}
                        className="p-4 flex items-center justify-between cursor-pointer bg-black/20"
                      >
                          <div className="flex items-center gap-4">
                              <div className={`p-3 rounded-xl ${record.roundCategory === 'FINAL' ? 'bg-yellow-900/30 text-yellow-500' : 'bg-zinc-800 text-zinc-400'}`}>
                                  {record.roundCategory === 'FINAL' ? <Trophy size={20} /> : <Layers size={20} />}
                              </div>
                              <div className="flex flex-col overflow-hidden">
                                  <div className="font-orbitron font-bold text-sm md:text-lg text-white truncate max-w-[200px]">
                                      {record.roundName || 'Standard Profile'}
                                  </div>
                                  <div className="text-[10px] text-zinc-500 font-rajdhani uppercase tracking-wider flex items-center gap-2">
                                      {getCategoryLabel(record.roundCategory)} • {formatDate(record.timestamp)}
                                  </div>
                              </div>
                          </div>

                          <div className="flex items-center gap-4">
                              <div className="text-right">
                                  <div className="text-[9px] text-zinc-600 uppercase tracking-widest font-orbitron">Total</div>
                                  <div className="text-2xl md:text-4xl font-black font-rajdhani text-yellow-500">{record.totalScore}</div>
                              </div>
                              {expandedId === record.id ? <ChevronUp size={20} className="text-zinc-600" /> : <ChevronDown size={20} className="text-zinc-600" />}
                          </div>
                      </div>

                      {/* Details (Expanded) */}
                      {expandedId === record.id && (
                          <div className="border-t border-zinc-800 bg-black/40 p-4 animate-in slide-in-from-top-2 duration-200">
                               {record.initialScore !== undefined && record.initialScore > 0 && (
                                   <div className="mb-4 flex justify-between items-center text-xs border-b border-zinc-800 pb-2">
                                       <span className="text-zinc-500 font-orbitron uppercase tracking-widest">Initial Final Score / 初始分</span>
                                       <span className="text-yellow-500 font-bold font-mono text-lg">+{record.initialScore}</span>
                                   </div>
                               )}
                               
                               <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                   {record.tricks.map((trick, idx) => (
                                       <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800/50">
                                           <div className="flex items-center gap-3 overflow-hidden">
                                               <span className="text-zinc-700 font-mono text-[10px] w-4">{idx + 1}</span>
                                               <span className={`font-rajdhani text-sm whitespace-pre-wrap ${trick.status === 'success' ? 'text-white' : 'text-zinc-500'}`}>
                                                   {trick.trickName}
                                               </span>
                                           </div>
                                           <div className="flex items-center gap-3 shrink-0">
                                               <span className="text-[10px] font-orbitron text-zinc-600">{trick.baseScore}pts</span>
                                               {getStatusIcon(trick.status)}
                                           </div>
                                       </div>
                                   ))}
                               </div>
                          </div>
                      )}
                  </div>
              ))
          )}
      </div>
    </div>
  );
};

export default TournamentHistory;
